import { FieldLabel } from 'core/components/common/FieldLabel'
import { IApplication } from 'core/interfaces/Application'
import { TagWithDeleteButton } from 'lauria/components/common/TagWithDeleteButton'
import { DeleteExternalMcpModal } from 'lauria/components/prompts/DeleteExternalMcpModal'
import { ExternalMcpCard } from 'lauria/components/prompts/ExternalMcpCard'
import { ExternalMcpFormModal } from 'lauria/components/prompts/ExternalMcpFormModal'
import { PromptAgentSelect } from 'lauria/components/prompts/PromptAgentSelect'
import { usePromptFormAdvancedSection } from 'lauria/hooks/usePromptFormAdvancedSection'
import {
  ExternalMcpFormValues,
  PromptExternalMcpItem,
} from 'lauria/types/externalMcp'
import { SelectedMcpApplication } from 'lauria/types/mcpApplication'
import React from 'react'
import { useTranslation } from 'react-i18next'

type PromptFormAdvancedSectionProps = {
  application: IApplication
  selectedAgents: SelectedMcpApplication[]
  setSelectedAgents: React.Dispatch<
    React.SetStateAction<SelectedMcpApplication[]>
  >
  externalMcps: PromptExternalMcpItem[]
  onAddExternalMcp: (values: ExternalMcpFormValues) => void
  onUpdateExternalMcp: (
    localId: string,
    values: Pick<ExternalMcpFormValues, 'authMethod' | 'url'>
  ) => void
  onDeleteExternalMcp: (localId: string) => void
}

export const PromptFormAdvancedSection: React.FC<
  PromptFormAdvancedSectionProps
> = ({
  application,
  selectedAgents,
  setSelectedAgents,
  externalMcps,
  onAddExternalMcp,
  onUpdateExternalMcp,
  onDeleteExternalMcp,
}) => {
  const { t } = useTranslation()
  const {
    handleAgentRemove,
    openCreateModal,
    openEditModal,
    openDeleteModal,
    formModal,
    deleteModal,
  } = usePromptFormAdvancedSection({
    setSelectedAgents,
    onAddExternalMcp,
    onUpdateExternalMcp,
    onDeleteExternalMcp,
  })

  return (
    <>
      <div className="prompt-form-fields-inner">
        <section className="prompt-form-advanced-section">
          <h3 className="prompt-form-advanced-section-title">
            {t('prompts.form.advanced.agents.sectionTitle')}
          </h3>
          <FieldLabel
            label={t('prompts.form.advanced.agents.label')}
            required
          />
          <PromptAgentSelect
            currentApplicationId={application.id}
            selectedAgents={selectedAgents}
            setSelectedAgents={setSelectedAgents}
            width="full"
          />
          {selectedAgents.length > 0 && (
            <div className="tags-list w-full">
              {selectedAgents.map((agent) => (
                <TagWithDeleteButton
                  key={agent.id}
                  tag={agent}
                  onClick={handleAgentRemove}
                />
              ))}
            </div>
          )}
        </section>

        <div className="prompt-form-advanced-divider" />

        <section className="prompt-form-advanced-section">
          <h3 className="prompt-form-advanced-section-title">
            {t('prompts.form.advanced.mcp.sectionTitle')}
          </h3>
          <FieldLabel label={t('prompts.form.advanced.mcp.label')} />
          <div className="external-mcp-list">
            {externalMcps.map((externalMcp) => (
              <ExternalMcpCard
                key={externalMcp.localId}
                url={externalMcp.url}
                onEdit={() => openEditModal(externalMcp)}
                onDelete={(event) => openDeleteModal(event, externalMcp)}
              />
            ))}
          </div>
          <button
            type="button"
            className="small-btn h-[40px] px-[10px] default"
            onClick={openCreateModal}
          >
            {t('prompts.form.advanced.mcp.addButton')}
          </button>
        </section>
      </div>

      <ExternalMcpFormModal
        open={formModal.open}
        mode={formModal.mode}
        initialValues={formModal.initialValues}
        onClose={formModal.onClose}
        onSubmit={formModal.onSubmit}
      />

      <DeleteExternalMcpModal
        open={deleteModal.open}
        onClose={deleteModal.onClose}
        onConfirm={deleteModal.onConfirm}
      />
    </>
  )
}
